import { Component, EventEmitter, Output, Inject, PLATFORM_ID, OnDestroy, OnInit } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { ActivatedRoute, Router } from '@angular/router';
import { CHAVE_MOTIVO_LOGOUT } from '../../shared/auth.interceptor';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../services/auth.service';
import { StorageService } from '../../services/storage.service';
import { SafeStorageService } from '../../shared/safe-storage.service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

import { UsuarioService } from '../../services/usuario/usuario.service';
import { UsuarioContextoDTO } from '../../models/usuario.model';
import { SocialLoginService } from '../../services/auth/social-login.service';
import { PermissaoService } from '../../services/permissao/permissao.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit, OnDestroy {
  // Constantes para localStorage
  private readonly REMEMBER_EMAIL_KEY = 'avati_remember_email';
  private readonly SAVED_EMAIL_KEY = 'avati_saved_email';

  // Telefone do suporte no WhatsApp (mesmo numero usado no app mobile)
  private readonly TELEFONE_SUPORTE = '595993290585';

  loginForm: FormGroup;
  wrongCredentials: boolean = false;
  submitted: boolean = false;
  rememberEmail: boolean = false;

  loadingGoogle = false;
  loadingApple = false;

  // Objetos de traducao
  mensagens = {
    erro: '',
    concluido: '',
    id_nao_retornado: '',
    empresa_nao_encontrada: '',
    autenticado: '',
    erro_carregar_usuario: '',
    credenciais_invalidas: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private fb: FormBuilder,
    private apiService: ApiService,
    private messageService: MessageService,
    private authService: AuthService,
    private storageService: StorageService,
    private router: Router,
    private route: ActivatedRoute,
    private safe: SafeStorageService,
    private usuarioService: UsuarioService,
    private translate: TranslateService,
    private socialLoginService: SocialLoginService,
    private permissaoService: PermissaoService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', Validators.required],
    });
  }

  ngOnInit() {
    const userId = this.storageService.getItem('id');
    if (userId) {
      this.router.navigateByUrl('/');
    }
    this.loadSavedEmail();
    this.loadTranslations();
    this.avisarSeSessaoFoiEncerrada();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  /**
   * Exibe a recusa padronizada do backend ({ message, codigo }) quando o motivo NAO e credencial
   * invalida — conta desativada (USUARIO_INATIVO), conta ja aberta em outra plataforma
   * (SESSAO_ATIVA) ou limite de usuarios do plano (ACESSO_NEGADO).
   *
   * @returns `true` quando a mensagem foi exibida e o chamador nao deve tratar o erro de novo.
   */
  private mostrarRecusaDeLogin(erro: any): boolean {
    const codigo = erro?.error?.codigo ?? erro?.error?.error;
    const mensagem = erro?.error?.message;
    if (!codigo || !mensagem || codigo === 'CREDENCIAIS_INVALIDAS') return false;

    this.messageService.add({
      severity: 'warn',
      summary: this.mensagens.erro,
      detail: mensagem,
      life: 6000,
    });
    return true;
  }

  /**
   * Explica a volta ao login quando o backend derrubou a sessao — token de 24h vencido (nao ha
   * renovacao) ou sessao encerrada em outro lugar. Sem isso o usuario e devolvido ao login sem
   * entender o motivo. O `AuthInterceptor` guarda a mensagem, ja traduzida, antes de redirecionar.
   */
  private avisarSeSessaoFoiEncerrada(): void {
    const mensagem = this.storageService.getItem(CHAVE_MOTIVO_LOGOUT);
    if (!mensagem) return;

    this.storageService.removeItem(CHAVE_MOTIVO_LOGOUT);
    this.messageService.add({
      severity: 'warn',
      summary: this.mensagens.erro,
      detail: mensagem,
      life: 6000,
    });
  }

  private loadTranslations(): void {
    this.mensagens = {
      erro: this.translate.instant('common.erro'),
      concluido: this.translate.instant('login.mensagens.concluido'),
      id_nao_retornado: this.translate.instant('login.mensagens.id_nao_retornado'),
      empresa_nao_encontrada: this.translate.instant('login.mensagens.empresa_nao_encontrada'),
      autenticado: this.translate.instant('login.mensagens.autenticado'),
      erro_carregar_usuario: this.translate.instant('login.mensagens.erro_carregar_usuario'),
      credenciais_invalidas: this.translate.instant('login.mensagens.credenciais_invalidas')
    };
  }

  /**
   * Carrega email salvo do localStorage (se existir)
   */
  private loadSavedEmail(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const shouldRemember = localStorage.getItem(this.REMEMBER_EMAIL_KEY) === 'true';
    const savedEmail = localStorage.getItem(this.SAVED_EMAIL_KEY);

    if (shouldRemember && savedEmail) {
      this.loginForm.patchValue({ email: savedEmail });
      this.rememberEmail = true;
    }
  }

  /**
   * Salva ou remove email do localStorage conforme checkbox
   */
  private handleRememberEmail(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const email = this.email?.value;

    if (this.rememberEmail && email) {
      localStorage.setItem(this.SAVED_EMAIL_KEY, email);
      localStorage.setItem(this.REMEMBER_EMAIL_KEY, 'true');
    } else {
      localStorage.removeItem(this.SAVED_EMAIL_KEY);
      localStorage.removeItem(this.REMEMBER_EMAIL_KEY);
    }
  }

  get email() {
    return this.loginForm.get('email');
  }

  get password() {
    return this.loginForm.get('password');
  }

  onSubmit() {
    this.submitted = true;
    if (!this.loginForm.valid || !this.email || !this.password) return;

    this.apiService.autenticar(this.email.value, this.password.value).subscribe({
      next: (response: any) => {
        // Guarda token se vier
        const authHeader = response?.headers?.get?.('Authorization');
        if (authHeader) this.safe.setItem('authToken', authHeader);

        const userId = response?.body?.id;
        if (userId !== undefined && userId !== null) {
          this.safe.setItem('userId', String(userId)); // Limpa ID antigo se houver
        }
        if (!userId && userId !== 0) {
          this.messageService.add({
            severity: 'error',
            summary: this.mensagens.erro,
            detail: this.mensagens.id_nao_retornado,
            life: 2500,
          });
          return;
        }

        // >>> NOVO: buscar /usuario/{id} e salvar empresaId no cache
        this.usuarioService.getContexto(`/usuario/${userId}`).subscribe({
          next: (usuario: UsuarioContextoDTO) => {
            const empresaId = usuario?.empresaId;
            if (empresaId === null || empresaId === undefined) {
              this.messageService.add({
                severity: 'error',
                summary: this.mensagens.erro,
                detail: this.mensagens.empresa_nao_encontrada,
                life: 2500,
              });
              return;
            }

            // Salva empresaId no cache no lugar do antigo "id"
            if (empresaId != null) {
              this.safe.setItem('id', String(empresaId)); // salva como string
            } else {
              console.warn('empresaId ausente na resposta do usuario:', usuario);
            }

            // Salvar email, categoriaAdmin e categoriaAcessoMaster no storage
            this.safe.setItem('email', usuario.email);
            if (usuario.categoriaAdmin != null) {
              this.safe.setItem('categoriaAdmin', String(usuario.categoriaAdmin));
            }
            if (usuario.categoriaAcessoMaster != null) {
              this.safe.setItem('categoriaAcessoMaster', String(usuario.categoriaAcessoMaster));
            }
            if (usuario.contador != null) {
              this.safe.setItem('contador', String(usuario.contador));
            }

            // Salva ou remove email do localStorage
            this.handleRememberEmail();

            this.authService.setAuthenticated(true);

            // Carregar permissoes e redirecionar inteligentemente
            this.permissaoService.carregarPermissoes(usuario.email).subscribe({
              next: () => {
                this.messageService.add({
                  severity: 'success',
                  summary: this.mensagens.concluido,
                  detail: this.mensagens.autenticado,
                  life: 2000,
                });
                const rota = this.permissaoService.obterPrimeiraRotaAcessivel();
                this.router.navigateByUrl(rota);
              },
              error: () => {
                // Fallback: navegar para rota padrao mesmo se permissoes falharem
                this.router.navigateByUrl('/');
              },
            });
          },
          error: (err: any) => {
            console.error('Erro ao buscar /usuario/{id}', err);
            this.messageService.add({
              severity: 'error',
              summary: this.mensagens.erro,
              detail: this.mensagens.erro_carregar_usuario,
              life: 2500,
            });
          },
        });
      },
      error: (error: any) => {
        // O backend responde toda recusa como { message, codigo }. Codigo diferente de
        // CREDENCIAIS_INVALIDAS significa que a senha esta certa e o motivo e outro (conta
        // desativada, conta ja aberta em outro navegador, limite do plano): a mensagem dele ja
        // vem traduzida e explica o que fazer, entao e ela que aparece.
        if (this.mostrarRecusaDeLogin(error)) return;

        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: this.mensagens.credenciais_invalidas,
          life: 2000,
        });
        this.wrongCredentials = true;
        console.log(error);
      },
    });
  }

  /**
   * Carrega permissoes e redireciona para a primeira rota acessivel.
   * Usado apos login social (Google/Apple).
   */
  private redirecionarComPermissoes(): void {
    const email = sessionStorage.getItem('email') || localStorage.getItem('email');
    if (email) {
      this.permissaoService.carregarPermissoes(email).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: this.mensagens.concluido,
            detail: this.mensagens.autenticado,
            life: 2000,
          });
          const rota = this.permissaoService.obterPrimeiraRotaAcessivel();
          this.router.navigateByUrl(rota);
        },
        error: () => {
          this.router.navigateByUrl('/');
        },
      });
    } else {
      // Sem email: fallback para rota padrao
      this.messageService.add({
        severity: 'success',
        summary: this.mensagens.concluido,
        detail: this.mensagens.autenticado,
        life: 2000,
      });
      this.router.navigateByUrl('/');
    }
  }

  /**
   * Login com Google Sign-In
   */
  loginComGoogle(): void {
    this.loadingGoogle = true;
    this.socialLoginService.loginComGoogle().subscribe({
      next: (resultado) => {
        this.loadingGoogle = false;
        if (resultado.tipo === 'login_ok') {
          this.redirecionarComPermissoes();
        } else if (resultado.tipo === 'novo_usuario') {
          this.router.navigate(['/signup'], {
            state: { socialData: resultado.dados }
          });
        }
      },
      error: (err) => {
        this.loadingGoogle = false;
        // Recusa do backend (conta desativada, conta ja aberta em outro navegador) tem mensagem
        // propria e nao deve virar o "erro ao entrar com Google" generico.
        if (this.mostrarRecusaDeLogin(err)) return;
        const errorMsg = err?.message === 'POPUP_CANCELLED'
          ? this.translate.instant('social_login.erro_cancelado')
          : err?.message === 'POPUP_BLOCKED'
            ? this.translate.instant('social_login.erro_popup_bloqueado')
            : this.translate.instant('social_login.erro_google');
        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: errorMsg,
          life: 3000,
        });
      }
    });
  }

  /**
   * Login com Apple Sign-In (via Firebase OAuthProvider)
   */
  loginComApple(): void {
    this.loadingApple = true;
    this.socialLoginService.loginComApple().subscribe({
      next: (resultado) => {
        this.loadingApple = false;
        if (resultado.tipo === 'login_ok') {
          this.redirecionarComPermissoes();
        } else if (resultado.tipo === 'novo_usuario') {
          this.router.navigate(['/signup'], {
            state: { socialData: resultado.dados }
          });
        }
      },
      error: (err) => {
        this.loadingApple = false;
        // Recusa do backend (conta desativada, conta ja aberta em outro navegador) tem mensagem
        // propria e nao deve virar o erro generico da Apple.
        if (this.mostrarRecusaDeLogin(err)) return;
        console.error('Apple Sign-In erro completo:', err);
        console.error('Apple Sign-In erro code:', err?.code);
        console.error('Apple Sign-In erro message:', err?.message);

        let errorMsg: string;
        const firebaseCode = err?.code || '';
        const errMessage = err?.message || '';

        if (firebaseCode === 'auth/operation-not-allowed') {
          errorMsg = 'Provedor Apple nao esta ativado no Firebase. Ative em Firebase Console > Authentication > Sign-in method > Apple.';
        } else if (firebaseCode === 'auth/popup-closed-by-user' || errMessage === 'POPUP_CANCELLED') {
          errorMsg = this.translate.instant('social_login.erro_cancelado');
        } else if (firebaseCode === 'auth/popup-blocked') {
          errorMsg = this.translate.instant('social_login.erro_popup_bloqueado');
        } else if (errMessage === 'APPLE_NO_EMAIL_NOT_REGISTERED') {
          errorMsg = this.translate.instant('social_login.erro_apple');
        } else {
          errorMsg = this.translate.instant('social_login.erro_apple') + ` (${firebaseCode || errMessage})`;
        }

        this.messageService.add({
          severity: 'error',
          summary: this.mensagens.erro,
          detail: errorMsg,
          life: 5000,
        });
      }
    });
  }

  /**
   * Abre o WhatsApp do suporte em uma nova aba com a mensagem padrao.
   */
  abrirSuporteWhatsApp(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const mensagem = this.translate.instant('login.suporte.mensagem');
    const url = `https://wa.me/${this.TELEFONE_SUPORTE}?text=${encodeURIComponent(mensagem)}`;
    window.open(url, '_blank');
  }
}
